import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import i18n from '../../../i18n';

const rows = [
  { topic: 'sllc_vs_r1_topic', without: 'sllc_vs_r1_without', with: 'sllc_vs_r1_with', icon: 'ri-bank-line' },
  { topic: 'sllc_vs_r2_topic', without: 'sllc_vs_r2_without', with: 'sllc_vs_r2_with', icon: 'ri-bar-chart-2-line' },
  { topic: 'sllc_vs_r3_topic', without: 'sllc_vs_r3_without', with: 'sllc_vs_r3_with', icon: 'ri-eye-line' },
  { topic: 'sllc_vs_r4_topic', without: 'sllc_vs_r4_without', with: 'sllc_vs_r4_with', icon: 'ri-shield-line' },
  { topic: 'sllc_vs_r5_topic', without: 'sllc_vs_r5_without', with: 'sllc_vs_r5_with', icon: 'ri-money-dollar-circle-line' },
  { topic: 'sllc_vs_r6_topic', without: 'sllc_vs_r6_without', with: 'sllc_vs_r6_with', icon: 'ri-rocket-line' },
];

export default function ComparisonSection() {
  const { t } = useTranslation();
  const isEs = i18n.language === 'es';

  return (
    <section className="py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-2xl mb-14">
          <span className="text-[#4F2D63] text-sm font-semibold uppercase tracking-widest">
            {t('sllc_vs_eyebrow')}
          </span>
          <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
            {t('sllc_vs_title')}
          </h2>
          <p className="mt-4 text-gray-500 text-base leading-relaxed">
            {t('sllc_vs_subtitle')}
          </p>
        </div>

        {/* Comparison Table */}
        <div className="rounded-xl overflow-hidden border border-[#E9D9F2]">
          {/* Column Headers */}
          <div className="grid grid-cols-[1fr_1fr_1fr] lg:grid-cols-[200px_1fr_1fr]">
            {/* Topic header — hidden on mobile */}
            <div className="hidden lg:flex items-end px-6 py-4 bg-gray-50 border-b border-r border-[#E9D9F2]">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Topic</span>
            </div>
            {/* Without header */}
            <div className="flex items-center gap-2.5 px-4 lg:px-6 py-4 bg-gray-50 border-b border-r border-[#E9D9F2]">
              <div className="w-7 h-7 flex items-center justify-center rounded-full bg-red-100 flex-shrink-0">
                <i className="ri-close-line text-red-500 text-sm" />
              </div>
              <span className="text-sm font-bold text-gray-700 leading-snug">
                {t('sllc_vs_without_label')}
              </span>
            </div>
            {/* With LLC header */}
            <div className="flex items-center gap-2.5 px-4 lg:px-6 py-4 bg-[#4F2D63] border-b border-[#3E234F]">
              <div className="w-7 h-7 flex items-center justify-center rounded-full bg-white/20 flex-shrink-0">
                <i className="ri-check-line text-white text-sm" />
              </div>
              <span className="text-sm font-bold text-white leading-snug">
                {t('sllc_vs_with_label')}
              </span>
            </div>
          </div>

          {/* Rows */}
          {rows.map((row, idx) => (
            <div
              key={row.topic}
              className={`grid grid-cols-[1fr_1fr] lg:grid-cols-[200px_1fr_1fr] border-b border-[#E9D9F2] last:border-b-0 ${
                idx % 2 === 0 ? 'bg-white' : 'bg-[#FDFBFF]'
              }`}
            >
              {/* Topic label — desktop only */}
              <div className="hidden lg:flex items-center gap-3 px-6 py-5 border-r border-[#E9D9F2]">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#F0E8F8] flex-shrink-0">
                  <i className={`${row.icon} text-[#4F2D63] text-sm`} />
                </div>
                <span className="text-xs font-semibold text-gray-600 leading-snug">
                  {t(row.topic)}
                </span>
              </div>

              {/* Without column */}
              <div className="px-4 lg:px-6 py-5 border-r border-[#E9D9F2]">
                {/* Mobile: show topic */}
                <p className="lg:hidden text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">
                  {t(row.topic)}
                </p>
                <div className="flex items-start gap-2">
                  <i className="ri-close-circle-line text-red-400 text-base mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-500 leading-relaxed">{t(row.without)}</p>
                </div>
              </div>

              {/* With LLC column */}
              <div className="px-4 lg:px-6 py-5 bg-[#F8F3FB]">
                {/* Mobile: show topic (hidden since already shown in without col) */}
                <div className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-[#4F2D63] text-base mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-800 font-medium leading-relaxed">{t(row.with)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <p className="text-sm text-gray-500 max-w-md leading-relaxed">
            {isEs
              ? 'El momento correcto para empezar es ahora — no después de esperar a "estar listo".'
              : 'The right time to build the foundation isn\'t "when you\'re ready" — it\'s right now.'}
          </p>
          <Link
            to={isEs ? '/es/contacto' : '/contact'}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#4F2D63] hover:bg-[#3E234F] text-white font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
          >
            {t('sllc_vs_cta')}
          </Link>
        </div>
      </div>
    </section>
  );
}
